import React, { useEffect, useState } from "react";
import "./AddAccountContactModal.css";
import { titles } from "constants/titles";
import CustomButton from "components/widgets/custom-button/CustomButton";
import CustomInput from "components/widgets/custom-input/CustomInput";
import CustomCheckbox from "components/widgets/custom-checkbox/CustomCheckbox";
import CustomSelectBox from "components/widgets/custom-select-box/CustomSelectBox";
import { formatPhoneNumber, isEmpty, isValidPhoneNumber } from "utility";
import alerts from "constants/alerts";
import { useDispatch, useSelector } from "react-redux";
import { getPhoneTypeList, setSelectedPhoneType } from "redux/Common/action";
import { useHistory } from "react-router-dom";

// Utility function to format phone type list
export const getAllPhoneTypeListNames = (allPhoneTypeList) => {
  const updateList = allPhoneTypeList?.map((ele) => {
    return {
      label: ele?.phone_type,
      value: ele?.phone_type,
      id: ele?.phone_type_id,
    };
  });
  return updateList;
};

const AddAccountContactModal = ({
  onSave,
  onClose,
  phoneInfo,
}) => {
  const [phoneNumberState, setPhoneNumberState] = useState({
    phoneNumber: "",
    type: "",
    isAcceptSignature: false,
  });

  // Mobile-like validation states
  const [fieldErrors, setFieldErrors] = useState({
    phoneNumber: false,
    phoneNumberInvalid: false,
    type: false
  });
  const [validationTriggered, setValidationTriggered] = useState(false);
  const { phoneTypeList = [], selectedPhoneType } = useSelector(
    (store) => store?.common
  );
  const dispatch = useDispatch();
  const history = useHistory();

  useEffect(() => {
    setSelectedPhoneType(dispatch, null);
    getPhoneTypeList({
      dispatch,
      history,
      userType: "accountowner",
    });
  }, [dispatch, history]);

  // Clear field error
  const clearFieldError = (fieldName) => {
    setFieldErrors(prev => ({
      ...prev,
      [fieldName]: false
    }));
  };

  useEffect(() => {
    onValueChange("phoneNumber", formatPhoneNumber(phoneInfo?.phoneNumber));
    onValueChange("isAcceptSignature", phoneInfo?.auto_notify);
    
    if (phoneInfo?.type) {
      onValueChange("type", phoneInfo?.type);
    } else if (selectedPhoneType) {
      onValueChange("type", selectedPhoneType);
    }
  }, [phoneInfo]);

  const setInitialState = () => {
    setPhoneNumberState({
      phoneNumber: "",
      type: "",
      isAcceptSignature: false,
    });
  };

  const onSaveChanges = () => {
    setValidationTriggered(true);

    setFieldErrors({ phoneNumber: false, phoneNumberInvalid: false, type: false });

    let hasErrors = false;
    const newFieldErrors = { phoneNumber: false, phoneNumberInvalid: false, type: false };

    if (isEmpty(phoneNumberState?.phoneNumber)) {
      newFieldErrors.phoneNumber = true;
      hasErrors = true;
    } else if (!isValidPhoneNumber(phoneNumberState?.phoneNumber)) {
      newFieldErrors.phoneNumberInvalid = true;
      hasErrors = true;
    }

    if (isEmpty(phoneNumberState?.type)) {
      newFieldErrors.type = true;
      hasErrors = true;
    }

    setFieldErrors(newFieldErrors);

    if (hasErrors) {
      return;
    }

    // Save if no errors
    if (onSave) {
      onSave(phoneNumberState);
    } else {
      onClose();
    }
    setInitialState();
    setValidationTriggered(false);
  };

  const onCancel = () => {
    onClose();
    setInitialState();
    setValidationTriggered(false);
    setFieldErrors({ phoneNumber: false, phoneNumberInvalid: false, type: false });
  };

  const onValueChange = (type, value) => {
    setPhoneNumberState((prev) => {
      return { ...prev, [type]: value };
    });
  };

  const handlePhoneNumberChange = (inputVal) => {
    const formattedPhoneNumber = formatPhoneNumber(inputVal);
    onValueChange("phoneNumber", formattedPhoneNumber);
    
    // Clear error when user starts typing (only after validation triggered)
    if (validationTriggered && fieldErrors.phoneNumber) {
      clearFieldError('phoneNumber');
    }
    if (validationTriggered && fieldErrors.phoneNumberInvalid) {
      clearFieldError('phoneNumberInvalid');
    }
  };

  const handlePhoneTypeDropdownSelect = (selectedOption, val, id) => {
    setSelectedPhoneType(dispatch, { phone_type: selectedOption, phone_type_id: id });
    onValueChange("type", { phone_type: selectedOption, phone_type_id: id });
    
    // Clear error when user selects (only after validation triggered)
    if (validationTriggered && fieldErrors.type) {
      clearFieldError('type');
    }
  };

  const modalContent = (
    <>
      {/* Main Form Section */}
      <div className="add_contact_content_section">
        {/* Form Title */}
        <p className="add_contact_form_title">
          {phoneInfo?.phoneNumber ? titles?.edit_contact : titles?.add_new_contact}
        </p>

        {/* Phone Number Input */}
        <CustomInput
          type="text"
          required
          placeholder="626-444-3333"
          value={phoneNumberState?.phoneNumber}
          onChange={(e) => {
            handlePhoneNumberChange(e?.target?.value);
          }}
          error={validationTriggered && (fieldErrors?.phoneNumber || fieldErrors?.phoneNumberInvalid)}
          errorMessage={
            validationTriggered && fieldErrors?.phoneNumberInvalid 
              ? alerts?.invalid_phone_number 
              : validationTriggered && fieldErrors?.phoneNumber 
                ? alerts?.incorrect_details 
                : ""
          }
        />

        {/* Contact Type Dropdown */}
        <CustomSelectBox
          title={titles?.select}
          required={true}
          className="mt15"
          selectedValue={phoneNumberState?.type?.phone_type}
          onChange={(item) => {
            handlePhoneTypeDropdownSelect(item?.label, item?.value, item?.id);
          }}
          options={getAllPhoneTypeListNames(phoneTypeList)}
          error={validationTriggered && fieldErrors?.type}
          errorMessage={validationTriggered && fieldErrors?.type ? alerts?.incorrect_details : ""}
        />

        {/* Test Message Permissions Section */}
        <div className="add_contact_permissions_section">
          <h3 className="add_contact_permissions_title">
            {titles?.text_message_permissions}
          </h3>
          
          <p className="add_contact_permissions_description">
            {titles?.text_message_permissions_dec}
          </p>

          <div className="add_contact_consent_container">
            <CustomCheckbox
              checked={phoneNumberState?.isAcceptSignature ? true : false}
              onChange={() => {
                onValueChange(
                  "isAcceptSignature",
                  !phoneNumberState?.isAcceptSignature
                );
              }}
            />
            <span className="add_contact_permissions_title">
              {titles?.accept_sms_mms}
            </span>
          </div>
          
          <p className="add_contact_permissions_description">
            {titles?.leave_unchecked}
          </p>
        </div>

        {/* Action Buttons */}
        <div className="add_contact_action_buttons">
          <CustomButton
            text={titles?.save_phone_contact}
            width={"100%"}
            onClick={() => {
              onSaveChanges();
            }}
            disabled={validationTriggered && (fieldErrors?.phoneNumber || fieldErrors?.phoneNumberInvalid || fieldErrors?.type)}
            showErrorMessage={validationTriggered && (fieldErrors?.phoneNumber || fieldErrors?.phoneNumberInvalid || fieldErrors?.type)}
          />
          <CustomButton
            text={titles?.cancel}
            width={"100%"}
            className="add_contact_cancel_button"
            onClick={() => {
              onCancel();
            }}
          />
        </div>
      </div>
    </>
  );

  return (
    <div className="add_contact_container">
      <div className="add_contact_body">
        {modalContent}
      </div>
    </div>
  );
};

export default AddAccountContactModal;
